/**
 * Demo Patient Generator Page
 * 
 * Admin tool for generating realistic demo patient data for executive demonstrations.
 * Features:
 * - Generate multiple patients at once
 * - View generated demo data
 * - Clear demo data
 * - Generate specific patient types
 * - Pre-built Demo Scenarios
 * - Demo Script Builder for custom sequences
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Users, UserPlus, Trash2, RefreshCw, Database, Activity,
  Heart, Brain, Stethoscope, AlertTriangle, CheckCircle,
  Play, Loader2, Download, Eye, Calendar, Pill, Zap,
  Target, FileText, Clock, Rocket, Plus, GripVertical,
  Save, FolderOpen, ArrowRight, Timer, Clapperboard, X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DIAGNOSIS_CATEGORIES = [
  { value: "cancer", label: "Cancer" },
  { value: "cardiac", label: "Cardiac (CHF)" },
  { value: "pulmonary", label: "Pulmonary (COPD)" },
  { value: "dementia", label: "Dementia" },
  { value: "renal", label: "Renal Disease" },
  { value: "hepatic", label: "Liver Disease" },
  { value: "neurological", label: "Neurological" },
  { value: "debility", label: "Debility/FTT" }
];

const LEVELS_OF_CARE = [
  { value: "Routine", label: "Routine Home Care" },
  { value: "GIP", label: "General Inpatient (GIP)" },
  { value: "Crisis", label: "Continuous Care (Crisis)" },
  { value: "Respite", label: "Respite Care" }
];

export default function DemoGeneratorPage() {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [stats, setStats] = useState(null);
  const [demoPatients, setDemoPatients] = useState([]);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  
  // Generation settings
  const [patientCount, setPatientCount] = useState(10);
  const [includeVisits, setIncludeVisits] = useState(true);
  const [includeTimeline, setIncludeTimeline] = useState(true);
  const [includeReferrals, setIncludeReferrals] = useState(true);
  const [clearExisting, setClearExisting] = useState(false);
  
  // Single patient generation
  const [showSingleDialog, setShowSingleDialog] = useState(false);
  const [singleDiagnosis, setSingleDiagnosis] = useState("");
  const [singleLOC, setSingleLOC] = useState("");
  const [singleDaysOnService, setSingleDaysOnService] = useState(30);

  // Demo Scenarios
  const [scenarios, setScenarios] = useState([]);
  const [deployingScenario, setDeployingScenario] = useState(null);
  const [deployedScenarios, setDeployedScenarios] = useState({});
  const [activeTab, setActiveTab] = useState("generator");

  // Script Builder
  const [prebuiltScripts, setPrebuiltScripts] = useState([]);
  const [savedScripts, setSavedScripts] = useState([]);
  const [scriptSteps, setScriptSteps] = useState([]);
  const [scriptName, setScriptName] = useState("");
  const [scriptDescription, setScriptDescription] = useState("");
  const [executingScript, setExecutingScript] = useState(null);
  const [executionProgress, setExecutionProgress] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Delay presets
  const DELAY_PRESETS = [
    { value: 0, label: "No delay" },
    { value: 3, label: "3 seconds" },
    { value: 5, label: "5 seconds" },
    { value: 10, label: "10 seconds" },
    { value: 30, label: "30 seconds" },
    { value: 60, label: "1 minute" }
  ];

  const fetchScripts = useCallback(async () => {
    try {
      const [prebuiltRes, savedRes] = await Promise.all([
        fetch(`${API}/api/demo-scenarios/scripts/prebuilt`),
        fetch(`${API}/api/demo-scenarios/scripts/saved`)
      ]);
      
      if (prebuiltRes.ok) {
        const data = await prebuiltRes.json();
        setPrebuiltScripts(data.scripts || []);
      }
      if (savedRes.ok) {
        const data = await savedRes.json();
        setSavedScripts(data.scripts || []);
      }
    } catch (error) {
      console.error("Failed to fetch scripts:", error);
    }
  }, []);

  const addStepToScript = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (!scenario) return;
    
    setScriptSteps(prev => [...prev, {
      scenario_id: scenarioId,
      scenario_name: scenario.name,
      delay_seconds: 5,
      description: scenario.name
    }]);
  };

  const removeStepFromScript = (index) => {
    setScriptSteps(prev => prev.filter((_, i) => i !== index));
  };

  const updateStepDelay = (index, delay) => {
    setScriptSteps(prev => prev.map((step, i) => 
      i === index ? { ...step, delay_seconds: parseInt(delay) || 0 } : step
    ));
  };

  const moveStep = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= scriptSteps.length) return;
    const newSteps = [...scriptSteps];
    const [removed] = newSteps.splice(fromIndex, 1);
    newSteps.splice(toIndex, 0, removed);
    setScriptSteps(newSteps);
  };

  const calculateTotalDuration = () => {
    return scriptSteps.reduce((total, step) => total + (step.delay_seconds || 0), 0);
  };

  const saveScript = async () => {
    if (!scriptName.trim() || scriptSteps.length === 0) {
      toast.error("Please add a name and at least one step");
      return;
    }

    try {
      const response = await fetch(`${API}/api/demo-scenarios/scripts/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: scriptName,
          description: scriptDescription,
          steps: scriptSteps.map(s => ({
            scenario_id: s.scenario_id,
            delay_seconds: s.delay_seconds,
            description: s.description
          }))
        })
      });

      if (response.ok) {
        toast.success("Script saved successfully!");
        setShowSaveDialog(false);
        setScriptName("");
        setScriptDescription("");
        fetchScripts();
      } else {
        toast.error("Failed to save script");
      }
    } catch (error) {
      toast.error("Error saving script");
      console.error(error);
    }
  };

  const deleteScript = async (scriptId) => {
    try {
      const response = await fetch(`${API}/api/demo-scenarios/scripts/${scriptId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Script deleted");
        fetchScripts();
      } else {
        toast.error("Failed to delete script");
      }
    } catch (error) {
      toast.error("Error deleting script");
      console.error(error);
    }
  };

  const executeScript = async (scriptId = null, steps = null) => {
    setExecutingScript(scriptId || "custom");
    setExecutionProgress({ step: 0, total: steps?.length || 0, status: "starting" });

    try {
      const body = scriptId 
        ? { script_id: scriptId }
        : { steps: steps.map(s => ({ scenario_id: s.scenario_id, delay_seconds: s.delay_seconds })) };

      const response = await fetch(`${API}/api/demo-scenarios/scripts/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setExecutionProgress({
          step: data.steps_executed,
          total: data.total_steps,
          status: data.success ? "complete" : "partial"
        });
        
        toast.success(`Executed ${data.steps_executed}/${data.total_steps} steps`, {
          description: `Total duration: ${data.total_duration_seconds}s`
        });
        
        fetchStats();
        fetchDemoPatients();
        
        // Clear progress after delay
        setTimeout(() => setExecutionProgress(null), 3000);
      } else {
        toast.error("Failed to execute script");
        setExecutionProgress(null);
      }
    } catch (error) {
      toast.error("Error executing script");
      console.error(error);
      setExecutionProgress(null);
    } finally {
      setExecutingScript(null);
    }
  };

  const loadScriptToBuilder = (script) => {
    setScriptSteps(script.steps.map(s => ({
      scenario_id: s.scenario_id,
      scenario_name: scenarios.find(sc => sc.id === s.scenario_id)?.name || s.scenario_id,
      delay_seconds: s.delay_seconds,
      description: s.description
    })));
    setActiveTab("scripts");
    toast.success(`Loaded "${script.name}" to builder`);
  };

  const fetchScenarios = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/demo-scenarios/available`);
      if (response.ok) {
        const data = await response.json();
        setScenarios(data.scenarios || []);
      }
    } catch (error) {
      console.error("Failed to fetch scenarios:", error);
    }
  }, []);

  const deployScenario = async (scenarioId) => {
    setDeployingScenario(scenarioId);
    try {
      const response = await fetch(`${API}/api/demo-scenarios/deploy/${scenarioId}`, {
        method: "POST"
      });

      if (response.ok) {
        const data = await response.json();
        setDeployedScenarios(prev => ({ ...prev, [scenarioId]: data }));
        toast.success(`Deployed: ${data.scenario.name}`, {
          description: "Demo data is ready for presentation"
        });
        fetchStats();
        fetchDemoPatients();
      } else {
        toast.error("Failed to deploy scenario");
      }
    } catch (error) {
      toast.error("Error deploying scenario");
      console.error(error);
    } finally {
      setDeployingScenario(null);
    }
  };

  const deployAllScenarios = async () => {
    setDeployingScenario("all");
    try {
      const response = await fetch(`${API}/api/demo-scenarios/deploy-all`, {
        method: "POST"
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Deployed ${data.scenarios_deployed} scenarios!`, {
          description: "All demo scenarios are ready"
        });
        fetchStats();
        fetchDemoPatients();
      } else {
        toast.error("Failed to deploy all scenarios");
      }
    } catch (error) {
      toast.error("Error deploying scenarios");
      console.error(error);
    } finally {
      setDeployingScenario(null);
    }
  };

  const clearAllScenarios = async () => {
    setClearing(true);
    try {
      const response = await fetch(`${API}/api/demo-scenarios/clear-all-scenarios`, {
        method: "DELETE"
      });

      if (response.ok) {
        const data = await response.json();
        setDeployedScenarios({});
        toast.success("Cleared all scenario data", {
          description: `Removed ${Object.values(data.deleted).reduce((a, b) => a + b, 0)} records`
        });
        fetchStats();
        fetchDemoPatients();
      } else {
        toast.error("Failed to clear scenarios");
      }
    } catch (error) {
      toast.error("Error clearing scenarios");
      console.error(error);
    } finally {
      setClearing(false);
    }
  };

  const getScenarioIcon = (iconName) => {
    const icons = {
      "alert-triangle": <AlertTriangle className="h-6 w-6 text-red-500" />,
      "user-plus": <UserPlus className="h-6 w-6 text-blue-500" />,
      "activity": <Activity className="h-6 w-6 text-purple-500" />,
      "heart": <Heart className="h-6 w-6 text-pink-500" />,
      "pill": <Pill className="h-6 w-6 text-green-500" />,
      "calendar": <Calendar className="h-6 w-6 text-amber-500" />,
      "users": <Users className="h-6 w-6 text-cyan-500" />
    };
    return icons[iconName] || <Target className="h-6 w-6 text-slate-500" />;
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/demo-generator/demo-stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  const fetchDemoPatients = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/demo-generator/demo-patients?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setDemoPatients(data.patients || []);
      }
    } catch (error) {
      console.error("Failed to fetch demo patients:", error);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStats(), fetchDemoPatients(), fetchScenarios(), fetchScripts()]).finally(() => setLoading(false));
  }, [fetchStats, fetchDemoPatients, fetchScenarios, fetchScripts]);

  const generatePatients = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/api/demo-generator/generate-patients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          count: patientCount,
          include_visits: includeVisits,
          include_timeline: includeTimeline,
          include_referrals: includeReferrals,
          clear_existing: clearExisting
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Generated ${data.summary.patients} demo patients!`, {
          description: `${data.summary.visits} visits, ${data.summary.referrals} referrals created`
        });
        fetchStats();
        fetchDemoPatients();
      } else {
        toast.error("Failed to generate patients");
      }
    } catch (error) {
      toast.error("Error generating patients");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const generateSinglePatient = async () => {
    setGenerating(true);
    try {
      const params = new URLSearchParams();
      if (singleDiagnosis && singleDiagnosis !== "random") params.append("diagnosis_category", singleDiagnosis);
      if (singleLOC && singleLOC !== "random") params.append("level_of_care", singleLOC);
      if (singleDaysOnService) params.append("days_on_service", singleDaysOnService);

      const response = await fetch(`${API}/api/demo-generator/generate-single?${params}`, {
        method: "POST"
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Generated patient: ${data.patient.name}`, {
          description: `${data.patient.primary_diagnosis} - ${data.patient.level_of_care}`
        });
        setShowSingleDialog(false);
        fetchStats();
        fetchDemoPatients();
      } else {
        toast.error("Failed to generate patient");
      }
    } catch (error) {
      toast.error("Error generating patient");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const clearDemoData = async () => {
    setClearing(true);
    try {
      const response = await fetch(`${API}/api/demo-generator/clear-demo-data`, {
        method: "DELETE"
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Demo data cleared", {
          description: `Removed ${data.details.patients} patients, ${data.details.visits} visits`
        });
        setShowClearDialog(false);
        fetchStats();
        fetchDemoPatients();
      } else {
        toast.error("Failed to clear demo data");
      }
    } catch (error) {
      toast.error("Error clearing demo data");
      console.error(error);
    } finally {
      setClearing(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "cancer": return <Activity className="h-4 w-4 text-rose-500" />;
      case "cardiac": return <Heart className="h-4 w-4 text-red-500" />;
      case "dementia": return <Brain className="h-4 w-4 text-purple-500" />;
      default: return <Stethoscope className="h-4 w-4 text-blue-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Database className="h-8 w-8 text-blue-600" />
              Demo Patient Generator
            </h1>
            <p className="text-slate-600 mt-1">
              Generate realistic hospice patient data for executive demonstrations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => { fetchStats(); fetchDemoPatients(); }}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowClearDialog(true)}
              disabled={!stats?.stats?.patients}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Demo Data
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Demo Patients</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats?.stats?.patients || 0}
                  </p>
                </div>
                <Users className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Demo Visits</p>
                  <p className="text-3xl font-bold text-green-600">
                    {stats?.stats?.visits || 0}
                  </p>
                </div>
                <Calendar className="h-10 w-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Timeline Events</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {stats?.stats?.timeline_events || 0}
                  </p>
                </div>
                <Activity className="h-10 w-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Referrals</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {stats?.stats?.referrals || 0}
                  </p>
                </div>
                <UserPlus className="h-10 w-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="generator" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Generator
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="flex items-center gap-2">
              <Rocket className="h-4 w-4" />
              Scenarios
            </TabsTrigger>
            <TabsTrigger value="scripts" className="flex items-center gap-2">
              <Clapperboard className="h-4 w-4" />
              Script Builder
            </TabsTrigger>
          </TabsList>

          {/* Patient Generator Tab */}
          <TabsContent value="generator">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Generator Panel */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="h-5 w-5 text-green-600" />
                    Generate Patients
                  </CardTitle>
                  <CardDescription>
                    Create demo patients with realistic hospice data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Batch Generation */}
                  <div className="space-y-4">
                    <div>
                      <Label>Number of Patients</Label>
                      <Input
                        type="number"
                        value={patientCount}
                        onChange={(e) => setPatientCount(parseInt(e.target.value) || 10)}
                        min={1}
                        max={50}
                        className="mt-1"
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Include Visit History</Label>
                        <Switch checked={includeVisits} onCheckedChange={setIncludeVisits} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Include Timeline Events</Label>
                        <Switch checked={includeTimeline} onCheckedChange={setIncludeTimeline} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Include Referrals</Label>
                        <Switch checked={includeReferrals} onCheckedChange={setIncludeReferrals} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-amber-600">Clear Existing First</Label>
                        <Switch checked={clearExisting} onCheckedChange={setClearExisting} />
                      </div>
                    </div>

                    <Button
                      className="w-full"
                      size="lg"
                      onClick={generatePatients}
                      disabled={generating}
                    >
                      {generating ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <Users className="h-5 w-5 mr-2" />
                      )}
                      Generate {patientCount} Patients
                    </Button>
                  </div>

                  <Separator />

                  {/* Single Patient */}
                  <div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowSingleDialog(true)}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Generate Single Patient
                    </Button>
                  </div>

                  {/* Distribution Charts */}
                  {stats?.diagnosis_distribution && Object.keys(stats.diagnosis_distribution).length > 0 && (
                    <>
                      <Separator />
                      <div>
                    <Label className="text-sm font-medium">Diagnosis Distribution</Label>
                    <div className="mt-2 space-y-2">
                      {Object.entries(stats.diagnosis_distribution).map(([category, count]) => (
                        <div key={category} className="flex items-center gap-2">
                          {getCategoryIcon(category)}
                          <span className="text-sm capitalize flex-1">{category}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Patient List */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Demo Patients ({demoPatients.length})
              </CardTitle>
              <CardDescription>
                Recently generated demo patients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Diagnosis</TableHead>
                      <TableHead>LOC</TableHead>
                      <TableHead>PPS</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>RNCM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {demoPatients.map((patient) => (
                      <TableRow key={patient.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{patient.name}</p>
                            <p className="text-xs text-slate-500">{patient.mrn}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(patient.diagnosis_category)}
                            <span className="text-sm">{patient.primary_diagnosis?.slice(0, 20)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            patient.level_of_care === "GIP" ? "destructive" :
                            patient.level_of_care === "Crisis" ? "warning" : "secondary"
                          }>
                            {patient.level_of_care}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={`font-medium ${
                            patient.pps_score <= 30 ? 'text-red-600' :
                            patient.pps_score <= 50 ? 'text-amber-600' : 'text-green-600'
                          }`}>
                            {patient.pps_score}%
                          </span>
                        </TableCell>
                        <TableCell>{patient.days_on_service}</TableCell>
                        <TableCell className="text-sm">{patient.assigned_rncm?.split(',')[0]}</TableCell>
                      </TableRow>
                    ))}
                    {demoPatients.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No demo patients generated yet. Click &quot;Generate Patients&quot; to create some.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
            </div>
          </TabsContent>

          {/* Demo Scenarios Tab */}
          <TabsContent value="scenarios">
            <div className="space-y-6">
              {/* Deploy All Banner */}
              <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <Rocket className="h-6 w-6" />
                        Pre-Built Demo Scenarios
                      </h3>
                      <p className="text-blue-100 mt-1">
                        One-click deployment of targeted demo data to showcase specific features
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="secondary"
                        onClick={clearAllScenarios}
                        disabled={clearing}
                      >
                        {clearing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        Clear All
                      </Button>
                      <Button
                        variant="default"
                        className="bg-white text-blue-600 hover:bg-blue-50"
                        onClick={deployAllScenarios}
                        disabled={deployingScenario === "all"}
                      >
                        {deployingScenario === "all" ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4 mr-2" />
                        )}
                        Deploy All Scenarios
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Scenarios Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {scenarios.map((scenario) => (
                  <Card 
                    key={scenario.id} 
                    className={`hover:shadow-lg transition-all duration-200 ${
                      deployedScenarios[scenario.id] ? 'ring-2 ring-green-500 bg-green-50' : ''
                    }`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {getScenarioIcon(scenario.icon)}
                          <div>
                            <CardTitle className="text-base">{scenario.name}</CardTitle>
                          </div>
                        </div>
                        {deployedScenarios[scenario.id] && (
                          <Badge className="bg-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Deployed
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="mt-2">
                        {scenario.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Demo Focus */}
                      <div>
                        <Label className="text-xs text-slate-500">SHOWCASES</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {scenario.demo_focus?.map((focus, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {focus}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Talking Points */}
                      <div>
                        <Label className="text-xs text-slate-500">TALKING POINTS</Label>
                        <ul className="mt-1 space-y-1">
                          {scenario.talking_points?.slice(0, 2).map((point, i) => (
                            <li key={i} className="text-xs text-slate-600 flex items-start gap-1">
                              <span className="text-green-500 mt-0.5">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Deploy Button */}
                      <Button
                        className="w-full"
                        variant={deployedScenarios[scenario.id] ? "outline" : "default"}
                        onClick={() => deployScenario(scenario.id)}
                        disabled={deployingScenario === scenario.id}
                      >
                        {deployingScenario === scenario.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : deployedScenarios[scenario.id] ? (
                          <RefreshCw className="h-4 w-4 mr-2" />
                        ) : (
                          <Play className="h-4 w-4 mr-2" />
                        )}
                        {deployedScenarios[scenario.id] ? "Redeploy" : "Deploy Scenario"}
                      </Button>
                    </CardContent>
                  </Card>
                ))}

                {scenarios.length === 0 && (
                  <Card className="col-span-full">
                    <CardContent className="py-12 text-center text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No scenarios available. Check backend connection.</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Scenario Usage Tips */}
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5" />
                    Demo Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-amber-700 space-y-2">
                  <p><strong>Before Demo:</strong> Deploy relevant scenarios 5 minutes before presentation.</p>
                  <p><strong>Critical Decline:</strong> Best for showing AI predictions and escalation workflows.</p>
                  <p><strong>New Admission:</strong> Perfect for demonstrating the 7-stage pipeline and documentation monitor.</p>
                  <p><strong>GIP Crisis:</strong> Showcases level of care changes and intensive symptom management.</p>
                  <p><strong>After Demo:</strong> Use &quot;Clear All&quot; to reset the system for next presentation.</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Script Builder Tab */}
          <TabsContent value="scripts">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Script Builder Panel */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clapperboard className="h-5 w-5 text-purple-600" />
                    Build Your Demo Script
                  </CardTitle>
                  <CardDescription>
                    Create a custom sequence of scenarios with timed delays
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Scenario Dropdown */}
                  <div className="flex gap-2">
                    <Select onValueChange={addStepToScript}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Add a scenario to script..." />
                      </SelectTrigger>
                      <SelectContent>
                        {scenarios.map(scenario => (
                          <SelectItem key={scenario.id} value={scenario.id}>
                            {scenario.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => addStepToScript(scenarios[0]?.id)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Script Timeline */}
                  <div className="border rounded-lg">
                    {scriptSteps.length === 0 ? (
                      <div className="py-12 text-center text-slate-500">
                        <Timer className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No steps added yet</p>
                        <p className="text-xs mt-1">Select scenarios above to build your script</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[320px]">
                        <div className="p-4 space-y-3">
                          {scriptSteps.map((step, index) => (
                            <div 
                              key={index}
                              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border"
                            >
                              {/* Step Number */}
                              <div className="flex flex-col items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => moveStep(index, index - 1)}
                                  disabled={index === 0}
                                >
                                  <GripVertical className="h-4 w-4 text-slate-400" />
                                </Button>
                                <Badge variant="outline" className="w-8 justify-center">
                                  {index + 1}
                                </Badge>
                              </div>

                              {/* Scenario Info */}
                              <div className="flex-1">
                                <p className="font-medium text-sm">{step.scenario_name}</p>
                                {index > 0 && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <Timer className="h-3 w-3 text-slate-400" />
                                    <Select
                                      value={String(step.delay_seconds)}
                                      onValueChange={(val) => updateStepDelay(index, val)}
                                    >
                                      <SelectTrigger className="h-7 w-28 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {DELAY_PRESETS.map(preset => (
                                          <SelectItem key={preset.value} value={String(preset.value)}>
                                            {preset.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <span className="text-xs text-slate-500">delay before</span>
                                  </div>
                                )}
                              </div>

                              {/* Remove Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                onClick={() => removeStepFromScript(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>

                  {/* Script Actions */}
                  {scriptSteps.length > 0 && (
                    <div className="flex items-center justify-between pt-2">
                      <div className="text-sm text-slate-600">
                        <span className="font-medium">{scriptSteps.length}</span> steps • 
                        <span className="font-medium ml-1">{calculateTotalDuration()}s</span> total delay
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowSaveDialog(true)}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save Script
                        </Button>
                        <Button
                          onClick={() => executeScript(null, scriptSteps)}
                          disabled={executingScript}
                        >
                          {executingScript === "custom" ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4 mr-2" />
                          )}
                          Execute Script
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Execution Progress */}
                  {executionProgress && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-800">
                          {executionProgress.status === "complete" ? "Execution Complete!" : "Executing..."}
                        </span>
                        <span className="text-sm text-blue-600">
                          {executionProgress.step}/{executionProgress.total} steps
                        </span>
                      </div>
                      <Progress 
                        value={(executionProgress.step / executionProgress.total) * 100} 
                        className="h-2"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Saved Scripts Panel */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-blue-600" />
                    Scripts Library
                  </CardTitle>
                  <CardDescription>
                    Pre-built and saved demo scripts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pre-built Scripts */}
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Pre-built</Label>
                    <div className="mt-2 space-y-2">
                      {prebuiltScripts.map(script => (
                        <div 
                          key={script.id}
                          className="p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{script.name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{script.description}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {script.steps?.length} steps
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {script.total_duration_seconds}s
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => loadScriptToBuilder(script)}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Load
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1"
                              onClick={() => executeScript(script.id)}
                              disabled={executingScript === script.id}
                            >
                              {executingScript === script.id ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3 mr-1" />
                              )}
                              Run
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Saved Scripts */}
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Your Scripts</Label>
                    <div className="mt-2 space-y-2">
                      {savedScripts.length === 0 ? (
                        <p className="text-sm text-slate-500 py-4 text-center">
                          No saved scripts yet
                        </p>
                      ) : (
                        savedScripts.map(script => (
                          <div 
                            key={script.id}
                            className="p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-sm">{script.name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="secondary" className="text-xs">
                                    {script.steps?.length} steps
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500"
                                onClick={() => deleteScript(script.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="flex gap-1 mt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => loadScriptToBuilder(script)}
                              >
                                <Download className="h-3 w-3 mr-1" />
                                Load
                              </Button>
                              <Button
                                size="sm"
                                className="flex-1"
                                onClick={() => executeScript(script.id)}
                                disabled={executingScript === script.id}
                              >
                                {executingScript === script.id ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Play className="h-3 w-3 mr-1" />
                                )}
                                Run
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Clear Confirmation Dialog */}
        <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Clear All Demo Data?
              </DialogTitle>
              <DialogDescription>
                This will permanently delete all demo patients, visits, timeline events, and referrals.
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Will be deleted:</strong>
                </p>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  <li>• {stats?.stats?.patients || 0} demo patients</li>
                  <li>• {stats?.stats?.visits || 0} visit records</li>
                  <li>• {stats?.stats?.timeline_events || 0} timeline events</li>
                  <li>• {stats?.stats?.referrals || 0} referrals</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowClearDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={clearDemoData} disabled={clearing}>
                {clearing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Yes, Clear All Demo Data
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Single Patient Dialog */}
        <Dialog open={showSingleDialog} onOpenChange={setShowSingleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-blue-600" />
                Generate Single Patient
              </DialogTitle>
              <DialogDescription>
                Create a specific patient type for targeted demos
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Diagnosis Category (optional)</Label>
                <Select value={singleDiagnosis} onValueChange={setSingleDiagnosis}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Random diagnosis" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Random</SelectItem>
                    {DIAGNOSIS_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Level of Care (optional)</Label>
                <Select value={singleLOC} onValueChange={setSingleLOC}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Random LOC" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Random</SelectItem>
                    {LEVELS_OF_CARE.map(loc => (
                      <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Days on Service</Label>
                <Input
                  type="number"
                  value={singleDaysOnService}
                  onChange={(e) => setSingleDaysOnService(parseInt(e.target.value) || 30)}
                  min={1}
                  max={365}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSingleDialog(false)}>
                Cancel
              </Button>
              <Button onClick={generateSinglePatient} disabled={generating}>
                {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Generate Patient
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save Script Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Save className="h-5 w-5 text-blue-600" />
                Save Demo Script
              </DialogTitle>
              <DialogDescription>
                Save this script for quick access during future demos
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Script Name</Label>
                <Input
                  value={scriptName}
                  onChange={(e) => setScriptName(e.target.value)}
                  placeholder="e.g., Full Executive Demo"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  value={scriptDescription}
                  onChange={(e) => setScriptDescription(e.target.value)}
                  placeholder="e.g., Complete walkthrough for C-suite presentations"
                  className="mt-1"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Script Summary</p>
                <div className="text-xs text-slate-600 space-y-1">
                  <p><strong>Steps:</strong> {scriptSteps.length}</p>
                  <p><strong>Total Delay:</strong> {calculateTotalDuration()} seconds</p>
                  <p><strong>Scenarios:</strong> {scriptSteps.map(s => s.scenario_name).join(" → ")}</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveScript} disabled={!scriptName.trim()}>
                <Save className="h-4 w-4 mr-2" />
                Save Script
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
